import React, { useState } from "react";
import { ButtonComp, CardComp } from "../../../components";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import ConfirmationModal from "../../../components/Common/ConfirmationModal";
import {
  useGetMemberExitEligibilityQuery,
  useGetMyMemberExitRequestQuery,
  useCreateMemberExitRequestMutation,
  useCancelMemberExitRequestMutation,
} from "../../../store/member/member-exit";
import { formatMoney } from "../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../utils/helpers";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";

const STATUS_COLORS = {
  PENDING: "#FFA500",
  APPROVED: "#31778C",
  DECLINED: "#FF4343",
  PAID: "#19BC31",
  CANCELED: "#A1A0A0",
};

function StatusBadge({ status }) {
  const key = (status || "").toUpperCase();
  return (
    <span
      className="px-3 py-1 rounded-pill text-white fw-semibold"
      style={{
        backgroundColor: STATUS_COLORS[key] || "#A1A0A0",
        fontSize: "12px",
        display: "inline-block",
        minWidth: "100px",
        textAlign: "center",
      }}
    >
      {convertToTitleCase(key || "Unknown")}
    </span>
  );
}

function AmountRow({ label, amount, hint, emphasize }) {
  return (
    <div
      className={`d-flex justify-content-between align-items-start py-2 ${
        emphasize ? "pt-3 mt-1 border-top" : "border-bottom"
      }`}
    >
      <div>
        <div
          className={emphasize ? "fw-bold text-black2" : "fw-semibold text-black2"}
          style={{ fontSize: emphasize ? "15px" : "14px" }}
        >
          {label}
        </div>
        {hint ? <div className="small text-blacklight mt-1">{hint}</div> : null}
      </div>
      <div
        className={emphasize ? "fw-bold text-primary" : "fw-bold text-black1"}
        style={{ fontSize: emphasize ? "16px" : "14px", whiteSpace: "nowrap" }}
      >
        {formatMoney(Number(amount || 0).toFixed(2), true, "₦")}
      </div>
    </div>
  );
}

function SuccessDialog({ isOpen, onClose, payoutAmount }) {
  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 10000 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-success-title"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 rounded-4 overflow-hidden">
          <div
            className="text-center text-white px-4 py-4"
            style={{
              background: "linear-gradient(135deg, #31778c 0%, #255a6b 100%)",
            }}
          >
            <div
              className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle bg-white"
              style={{ width: 56, height: 56 }}
              aria-hidden="true"
            >
              <span style={{ color: "#19BC31", fontSize: 28, fontWeight: 700 }}>
                ✓
              </span>
            </div>
            <h5 id="exit-success-title" className="fw-bold mb-1">
              Exit request submitted
            </h5>
            <p className="mb-0 small" style={{ opacity: 0.92 }}>
              Administrators will review your request shortly
            </p>
          </div>
          <div className="modal-body px-4 py-4">
            <p className="text-black1 mb-3">
              Your membership exit request has been received and is now{" "}
              <strong>pending review</strong>.
            </p>
            <div
              className="rounded-3 p-3 mb-3"
              style={{ background: "#f7f7f7", border: "1px solid #e0e0e0" }}
            >
              <div className="small text-blacklight mb-1">Estimated payout</div>
              <div className="fw-bold text-black2" style={{ fontSize: 20 }}>
                {formatMoney(Number(payoutAmount || 0).toFixed(2), true, "₦")}
              </div>
              <div className="small text-blacklight mt-2">
                Net savings + shares. Other contributions are not refunded.
              </div>
            </div>
            <ul className="small text-blacklight mb-0 ps-3">
              <li className="mb-1">You can cancel while the request is still pending.</li>
              <li className="mb-1">You will be notified when it is approved or declined.</li>
              <li>After payout is completed, you will no longer be able to sign in.</li>
            </ul>
          </div>
          <div className="modal-footer border-0 px-4 pb-4 pt-0">
            <ButtonComp
              btnText="Got it"
              btnClassName="border-0 text-white py-2 px-5 w-100 rounded"
              ButtonInlineStyle={{
                backgroundColor: "#8b313a",
                minWidth: "100%",
                borderRadius: 10,
              }}
              onClick={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MemberExitPanel() {
  const [note, setNote] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedPayout, setSubmittedPayout] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const {
    data: eligibility,
    isLoading: eligibilityLoading,
    isFetching: eligibilityFetching,
  } = useGetMemberExitEligibilityQuery();
  const { data: exitRequest, isLoading: requestLoading } =
    useGetMyMemberExitRequestQuery();
  const [createRequest, { isLoading: isSubmitting }] =
    useCreateMemberExitRequestMutation();
  const [cancelRequest, { isLoading: isCanceling }] =
    useCancelMemberExitRequestMutation();

  const loading = eligibilityLoading || requestLoading || eligibilityFetching;
  const status = (exitRequest?.status || "").toUpperCase();
  const hasOpenRequest = ["PENDING", "APPROVED"].includes(status);

  const handleSubmitConfirm = async () => {
    try {
      const payout = eligibility?.payout_amount ?? 0;
      await createRequest({ member_note: note || null }).unwrap();
      setNote("");
      setShowSubmitConfirm(false);
      setSubmittedPayout(payout);
      setShowSuccess(true);
    } catch (error) {
      setShowSubmitConfirm(false);
      toast.error(
        error?.data?.message || error?.message || "Failed to submit exit request"
      );
    }
  };

  const handleCancelConfirm = async () => {
    if (!exitRequest?.id) return;
    try {
      await cancelRequest(exitRequest.id).unwrap();
      setShowCancelConfirm(false);
      toast.success("Your exit request has been canceled.");
    } catch (error) {
      toast.error(
        error?.data?.message || error?.message || "Failed to cancel request"
      );
    }
  };

  if (loading && !eligibility) {
    return (
      <div aria-busy="true" aria-label="Loading membership exit details">
        <TableSkeletonLoader rows={8} columns={2} />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-3">
      <CardComp cardClassName="rounded-4 border shadow-none">
        <div className="p-3 p-md-4">
          <h6 className="fw-bold text-black2 mb-2">Request membership exit</h6>
          <p className="text-blacklight small mb-0" style={{ lineHeight: 1.6 }}>
            If approved, you will receive your <strong>net savings</strong> and{" "}
            <strong>full shares</strong>. Other contributions are shown for
            transparency but are <strong>not refunded</strong>. After exit is
            completed, you will no longer be able to sign in.
          </p>
        </div>
      </CardComp>

      {exitRequest && status ? (
        <CardComp cardClassName="rounded-4 border shadow-none">
          <div className="p-3 p-md-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h6 className="fw-bold mb-0">Current request status</h6>
              <StatusBadge status={status} />
            </div>

            <div
              className="rounded-3 p-3 mb-3"
              style={{ background: "#fafafa", border: "1px solid #e0e0e0" }}
            >
              <div className="row g-3">
                <div className="col-sm-6">
                  <div className="small text-blacklight">Submitted</div>
                  <div className="fw-semibold text-black2">
                    {exitRequest.created_at
                      ? moment(exitRequest.created_at).format("DD-MMM-YYYY")
                      : "—"}
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="small text-blacklight">Estimated payout</div>
                  <div className="fw-bold text-black2" style={{ fontSize: 18 }}>
                    {formatMoney(
                      Number(exitRequest.payout_amount || 0).toFixed(2),
                      true,
                      "₦"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {exitRequest.decline_reason && (
              <div
                className="rounded-3 p-3 mb-3"
                style={{
                  background: "rgba(255, 67, 67, 0.08)",
                  border: "1px solid #e0a8ae",
                }}
              >
                <div className="small fw-semibold text-danger mb-1">
                  Decline reason
                </div>
                <p className="small text-black1 mb-0">{exitRequest.decline_reason}</p>
              </div>
            )}

            {status === "PENDING" && (
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <p className="small text-blacklight mb-0 me-auto">
                  You can cancel this request while it is still pending review.
                </p>
                <ButtonComp
                  btnText="Cancel request"
                  loading={isCanceling}
                  btnClassName="py-2 px-4"
                  BorderColor="#FF4343"
                  ButtonInlineStyle={{
                    color: "#FF4343",
                    minWidth: 160,
                    borderRadius: 10,
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => setShowCancelConfirm(true)}
                />
              </div>
            )}

            {status === "APPROVED" && (
              <p className="small text-blacklight mb-0">
                Your request is approved. Staff will process your payout shortly.
                You can no longer cancel this request.
              </p>
            )}

            {status === "DECLINED" && (
              <p className="small text-blacklight mb-0">
                This request was declined. You may submit a new request once you
                meet the eligibility requirements.
              </p>
            )}

            {status === "PAID" && (
              <p className="small mb-0" style={{ color: "#19BC31" }}>
                Exit completed. Access to this cooperative has ended.
              </p>
            )}

            {status === "CANCELED" && (
              <p className="small text-blacklight mb-0">
                You canceled this request. You can submit a new one when eligible.
              </p>
            )}
          </div>
        </CardComp>
      ) : null}

      <CardComp cardClassName="rounded-4 border shadow-none">
        <div className="p-3 p-md-4">
          <h6 className="fw-bold mb-3">Settlement summary</h6>
          <AmountRow label="Savings (net)" amount={eligibility?.total_savings} />
          <AmountRow label="Shares" amount={eligibility?.total_shares} />
          <AmountRow
            label="Other"
            amount={eligibility?.total_other}
            hint="Shown for transparency — not included in payout"
          />
          <AmountRow
            label="Estimated payout"
            amount={eligibility?.payout_amount}
            emphasize
          />
          <AmountRow
            label="Outstanding loans"
            amount={eligibility?.outstanding_loan_amount}
          />
          <AmountRow
            label="Unpaid fines"
            amount={eligibility?.outstanding_fine_amount}
          />
        </div>
      </CardComp>

      {!eligibility?.can_request_exit && !hasOpenRequest && (
        <CardComp cardClassName="rounded-4 border shadow-none">
          <div
            className="p-3 p-md-4 rounded-4"
            style={{ background: "rgba(255, 149, 0, 0.08)" }}
          >
            <h6 className="fw-bold text-black2 mb-2">Cannot request exit yet</h6>
            <p className="small text-blacklight mb-3">
              Clear the items below, then return here to submit your exit request.
            </p>
            <ul className="mb-3 ps-3">
              {(eligibility?.reasons || []).map((reason) => (
                <li key={reason} className="small text-black1 mb-2">
                  {reason}
                </li>
              ))}
            </ul>
            <div className="d-flex flex-wrap gap-2">
              {(eligibility?.outstanding_loan_amount || 0) > 0 && (
                <Link to="/member/loan">
                  <ButtonComp
                    btnText="Go to loans"
                    btnClassName="py-2 px-3"
                    BorderColor="#31778C"
                    ButtonInlineStyle={{ color: "#31778C", minWidth: 140 }}
                  />
                </Link>
              )}
              {(eligibility?.outstanding_fine_amount || 0) > 0 && (
                <Link to="/member/attendance">
                  <ButtonComp
                    btnText="Go to fines"
                    btnClassName="py-2 px-3"
                    BorderColor="#31778C"
                    ButtonInlineStyle={{ color: "#31778C", minWidth: 140 }}
                  />
                </Link>
              )}
              {!eligibility?.has_bank_account && (
                <Link to="/member/profile">
                  <ButtonComp
                    btnText="Add bank account"
                    btnClassName="py-2 px-3"
                    BorderColor="#31778C"
                    ButtonInlineStyle={{ color: "#31778C", minWidth: 160 }}
                  />
                </Link>
              )}
            </div>
          </div>
        </CardComp>
      )}

      {eligibility?.can_request_exit && !hasOpenRequest && (
        <CardComp cardClassName="rounded-4 border shadow-none">
          <div className="p-3 p-md-4">
            <h6 className="fw-bold mb-3">Confirm and submit</h6>

            <div className="small text-blacklight mb-1">Payout bank account</div>
            {eligibility?.bank_account ? (
              <div
                className="rounded-3 p-3 mb-3"
                style={{ background: "#fafafa", border: "1px solid #e0e0e0" }}
              >
                <div className="fw-semibold text-black2">
                  {eligibility.bank_account.bank_name}
                </div>
                <div className="small text-black1 mt-1">
                  {eligibility.bank_account.account_number} ·{" "}
                  {eligibility.bank_account.account_name}
                </div>
              </div>
            ) : (
              <p className="small text-danger mb-3">No bank account on file.</p>
            )}

            <label className="fw-semibold text-black2 mb-1 d-block" htmlFor="exit-note">
              Note for administrators{" "}
              <span className="fw-normal text-blacklight">(optional)</span>
            </label>
            <textarea
              id="exit-note"
              className="form-control"
              rows={3}
              placeholder="e.g. Preferred payout date or any extra information"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ background: "#fafafa", borderColor: "#a1a0a0", borderRadius: 8 }}
            />

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-4">
              <div className="small text-blacklight">
                Estimated payout:{" "}
                <strong className="text-black2">
                  {formatMoney(
                    Number(eligibility?.payout_amount || 0).toFixed(2),
                    true,
                    "₦"
                  )}
                </strong>
              </div>
              <ButtonComp
                btnText="Submit exit request"
                loading={isSubmitting}
                btnClassName="border-0 text-white py-2 px-4"
                ButtonInlineStyle={{
                  backgroundColor: "#8b313a",
                  minWidth: 200,
                  borderRadius: 10,
                  whiteSpace: "nowrap",
                }}
                onClick={() => setShowSubmitConfirm(true)}
              />
            </div>
          </div>
        </CardComp>
      )}

      <ConfirmationModal
        isOpen={showSubmitConfirm}
        onClose={() => !isSubmitting && setShowSubmitConfirm(false)}
        onConfirm={handleSubmitConfirm}
        title="Submit membership exit?"
        message={
          <div>
            <p className="mb-2">
              You are about to request membership exit with an estimated payout of{" "}
              <strong>
                {formatMoney(
                  Number(eligibility?.payout_amount || 0).toFixed(2),
                  true,
                  "₦"
                )}
              </strong>
              .
            </p>
            <p className="mb-0 small">
              Staff will review your request. You can cancel while it is still
              pending.
            </p>
          </div>
        }
        confirmText="Yes, submit request"
        cancelText="Go back"
        confirmButtonClass="text-white border-0"
        cancelButtonClass="bg-white"
        confirmButtonStyle={{ backgroundColor: "#8b313a", borderRadius: 10 }}
        cancelButtonStyle={{
          color: "#8b313a",
          border: "2px solid #8b313a",
          borderRadius: 10,
          backgroundColor: "#fff",
        }}
        isLoading={isSubmitting}
      />

      <ConfirmationModal
        isOpen={showCancelConfirm}
        onClose={() => !isCanceling && setShowCancelConfirm(false)}
        onConfirm={handleCancelConfirm}
        title="Cancel exit request?"
        message="This will withdraw your pending membership exit request. You can submit a new one later if you remain eligible."
        confirmText="Yes, cancel request"
        cancelText="Keep request"
        confirmButtonClass="text-white border-0"
        cancelButtonClass="bg-white"
        confirmButtonStyle={{ backgroundColor: "#FF4343", borderRadius: 10 }}
        cancelButtonStyle={{
          color: "#8b313a",
          border: "2px solid #8b313a",
          borderRadius: 10,
          backgroundColor: "#fff",
        }}
        isLoading={isCanceling}
      />

      <SuccessDialog
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        payoutAmount={submittedPayout || exitRequest?.payout_amount}
      />
    </div>
  );
}
